import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Package, PencilSimple } from "@phosphor-icons/react";

const STATUS = {
  draft: "bg-navy/10 text-navy", submitted: "bg-warn/15 text-warn", under_review: "bg-warn/15 text-warn",
  published: "bg-ok/15 text-ok", rejected: "bg-destructive/10 text-destructive",
  suspended: "bg-destructive/10 text-destructive", sold: "bg-navy text-white", closed: "bg-navy/10 text-navy",
};
const LABEL = { under_review: "Under Review" };

export default function SellerDashboard() {
  const { user } = useAuth();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/seller/lots").then(({ data }) => setLots(data.lots)).finally(() => setLoading(false)); }, []);

  const counts = lots.reduce((a, l) => { a[l.status] = (a[l.status] || 0) + 1; return a; }, {});
  const totalValue = lots.filter((l) => l.status === "published").reduce((s, l) => s + l.mrp_value, 0);

  return (
    <div className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Seller dashboard</span>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-navy">{user?.company || user?.name}</h1>
        </div>
        <Button asChild className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="new-lot-button"><Link to="/seller/new"><Plus className="mr-1" /> Submit new lot</Link></Button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border md:grid-cols-4">
        {[
          { k: lots.length, l: "Total lots" },
          { k: counts.published || 0, l: "Published" },
          { k: (counts.under_review || 0) + (counts.submitted || 0), l: "In review" },
          { k: inr(totalValue), l: "Live MRP value" },
        ].map((s) => (
          <div key={s.l} className="bg-navy-50 p-5"><div className="stat text-2xl font-bold text-navy">{s.k}</div><div className="text-xs uppercase tracking-wider text-muted-foreground">{s.l}</div></div>
        ))}
      </div>

      <h2 className="mt-10 font-display text-xl font-bold text-navy">My lots</h2>
      {loading ? (
        <div className="mt-10 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-navy/20 border-t-brand" /></div>
      ) : lots.length === 0 ? (
        <div className="mt-6 rounded-sm border border-dashed border-border p-16 text-center">
          <Package size={40} className="mx-auto text-navy/20" />
          <p className="mt-4 font-display text-lg font-semibold text-navy">No lots submitted yet</p>
          <p className="mt-1 text-muted-foreground">Submit your first bulk lot for verification.</p>
          <Button asChild className="mt-4 rounded-sm bg-brand text-white hover:bg-brand-hover"><Link to="/seller/new">Submit a lot</Link></Button>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-sm border border-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-navy text-white text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Lot</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Units</th><th className="px-4 py-3 text-right">MRP</th><th className="px-4 py-3 text-right">Asking</th><th className="px-4 py-3 text-right">Views</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lots.map((l) => (
                <tr key={l.id} className="bg-white hover:bg-navy-50" data-testid={`seller-lot-${l.id}`}>
                  <td className="px-4 py-3 font-medium text-navy max-w-[280px]">
                    <span className="line-clamp-1">{l.title}</span>
                    {l.status === "rejected" && l.rejection_reason && <span className="mt-0.5 block text-xs text-destructive">Reason: {l.rejection_reason}</span>}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-wider ${STATUS[l.status]}`}>{LABEL[l.status] || l.status}</span></td>
                  <td className="stat px-4 py-3 text-right">{num(l.total_units)}</td>
                  <td className="stat px-4 py-3 text-right">{inr(l.mrp_value)}</td>
                  <td className="stat px-4 py-3 text-right text-brand font-bold">{inr(l.asking_price)}</td>
                  <td className="stat px-4 py-3 text-right">{num(l.views)}</td>
                  <td className="px-4 py-3 text-right">
                    {["draft", "rejected", "under_review", "submitted"].includes(l.status) ? (
                      <Link to={`/seller/edit/${l.id}`} className="inline-flex items-center gap-1 font-semibold text-navy hover:text-brand" data-testid={`edit-lot-${l.id}`}><PencilSimple size={14} /> Edit</Link>
                    ) : (
                      <Link to={`/lots/${l.id}`} className="font-semibold text-navy hover:text-brand">View</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
